import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

// Connects to data-controller="interview--live-assistance"
export default class extends Controller {
  static targets = [
    "transcript", 
    "input", 
    "status", 
    "suggestions", 
    "insights", 
    "controls",
    "timer"
  ]
  
  static values = {
    id: String,
    sessionToken: String,
    started: Boolean,
    status: String,
    refreshInterval: { type: Number, default: 5000 }
  }

  connect() {
    this.setupRefreshTimers()
    this.subscribeToChannel()
    this.updateControlsVisibility()
    
    if (this.startedValue) {
      this.startTimer()
    }
  }
  
  disconnect() {
    this.clearRefreshTimers()
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    this.stopTimer()
  }
  
  startSession(event) {
    event.preventDefault()
    
    fetch(`/assistances/${this.idValue}/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCSRFToken()
      },
      body: JSON.stringify({})
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        this.statusValue = data.status
        this.startedValue = true
        this.startTimer()
        this.updateControlsVisibility()
      }
    })
    .catch(error => this.handleError('Error:', error))
  }
  
  pauseSession(event) {
    event.preventDefault()
    
    fetch(`/assistances/${this.idValue}/pause`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCSRFToken()
      },
      body: JSON.stringify({})
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        this.statusValue = data.status
        this.updateControlsVisibility()
        this.pauseTimer()
      }
    })
    .catch(error => this.handleError('Error:', error))
  }
  
  resumeSession(event) {
    event.preventDefault()
    
    fetch(`/assistances/${this.idValue}/resume`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCSRFToken()
      },
      body: JSON.stringify({})
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        this.statusValue = data.status
        this.updateControlsVisibility()
        this.resumeTimer()
      }
    })
    .catch(error => this.handleError('Error:', error))
  }
  
  completeSession(event) {
    event.preventDefault()
    
    if (!confirm('Are you sure you want to complete this interview session?')) {
      return
    }
    
    fetch(`/assistances/${this.idValue}/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCSRFToken()
      },
      body: JSON.stringify({})
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        this.statusValue = data.status
        this.updateControlsVisibility()
        this.stopTimer()
      }
    })
    .catch(error => this.handleError('Error:', error))
  }
  
  addTranscriptSegment(event) {
    event.preventDefault()
    
    // Get input values
    const speaker = event.currentTarget.querySelector('input[name="transcript[speaker]"]').value
    const text = this.inputTarget.value
    
    // Validate input
    if (!text.trim()) {
      alert('Please enter some text')
      return
    }
    
    // Add segment to transcript via API
    fetch(`/assistances/${this.idValue}/transcript`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCSRFToken()
      },
      body: JSON.stringify({
        transcript: {
          speaker,
          text
        }
      })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        // Clear input
        this.inputTarget.value = ''
        
        // Refresh suggestions after a delay if this was a candidate segment
        if (speaker === 'candidate') {
          setTimeout(() => this.refreshSuggestions(), 1000)
        }
      }
    })
    .catch(error => this.handleError('Error:', error))
  }
  
  refreshSuggestions() {
    fetch(`/assistances/${this.idValue}/suggestions`, {
      headers: {
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      this.renderSuggestions(data)
    })
    .catch(error => this.handleError('Error refreshing suggestions:', error))
  }
  
  refreshInsights() {
    fetch(`/assistances/${this.idValue}/insights`, {
      headers: {
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      this.renderInsights(data)
    })
    .catch(error => this.handleError('Error refreshing insights:', error))
  }
  
  copySuggestion(event) {
    const question = event.currentTarget.getAttribute('data-question')
    if (question) {
      this.inputTarget.value = question
      this.inputTarget.focus()
    }
  }
  
  // Private methods
  
  setupRefreshTimers() {
    this.suggestionsTimer = setInterval(() => {
      this.refreshSuggestions()
    }, this.refreshIntervalValue)
    
    this.insightsTimer = setInterval(() => {
      this.refreshInsights()
    }, this.refreshIntervalValue * 2)
  }
  
  clearRefreshTimers() {
    clearInterval(this.suggestionsTimer)
    clearInterval(this.insightsTimer)
  }
  
  subscribeToChannel() {
    const consumer = createConsumer()
    
    this.subscription = consumer.subscriptions.create(
      {
        channel: "InterviewAssistanceChannel",
        id: this.idValue,
        token: this.sessionTokenValue
      },
      {
        connected: () => {
          // Connected to InterviewAssistanceChannel
        },
        disconnected: () => {
          // Disconnected from InterviewAssistanceChannel
        },
        received: data => {
          this.handleChannelMessage(data)
        }
      }
    )
  }
  
  handleChannelMessage(data) {
    if (data.transcript_segment) {
      this.appendTranscriptSegment(data.transcript_segment)
    }
    
    if (data.suggestion) {
      this.addSuggestion(data.suggestion)
    }
    
    if (data.insight) {
      this.addInsight(data.insight)
    }
    
    if (data.status) {
      this.statusValue = data.status
      this.updateControlsVisibility()
    }
  }
  
  appendTranscriptSegment(segment) {
    // Create new segment element
    const segmentDiv = document.createElement('div')
    segmentDiv.className = `p-3 ${segment.speaker === 'interviewer' ? 'bg-blue-50' : 'bg-green-50'} rounded mb-2`
    
    const speakerSpan = document.createElement('span')
    speakerSpan.className = 'font-semibold'
    speakerSpan.textContent = segment.speaker === 'interviewer' ? 'Interviewer: ' : 'Candidate: '
    
    const textSpan = document.createElement('span')
    textSpan.textContent = segment.text
    
    segmentDiv.appendChild(speakerSpan)
    segmentDiv.appendChild(textSpan)
    
    // Add to transcript
    this.transcriptTarget.appendChild(segmentDiv)
    
    // Scroll to bottom
    this.transcriptTarget.scrollTop = this.transcriptTarget.scrollHeight
  }
  
  renderSuggestions(suggestions) {
    // Clear existing suggestions
    this.suggestionsTarget.innerHTML = ''
    
    if (suggestions.length === 0) {
      const emptyDiv = document.createElement('div')
      emptyDiv.className = 'p-4 text-gray-500 italic'
      emptyDiv.textContent = 'No suggestions available yet.'
      this.suggestionsTarget.appendChild(emptyDiv)
      return
    }
    
    // Add each suggestion
    suggestions.forEach(suggestion => {
      this.addSuggestion(suggestion)
    })
  }
  
  addSuggestion(suggestion) {
    const suggestionDiv = document.createElement('div')
    suggestionDiv.className = 'p-3 bg-yellow-50 rounded mb-2 group hover:bg-yellow-100'
    
    const questionDiv = document.createElement('div')
    questionDiv.className = 'font-medium text-gray-800 mb-1'
    questionDiv.textContent = suggestion.question
    
    const reasoningDiv = document.createElement('div')
    reasoningDiv.className = 'text-sm text-gray-600'
    reasoningDiv.textContent = suggestion.reasoning
    
    const copyButton = document.createElement('button')
    copyButton.type = 'button'
    copyButton.className = 'mt-2 text-sm bg-white text-blue-600 px-2 py-1 rounded border border-blue-300 hover:bg-blue-50 opacity-0 group-hover:opacity-100 transition-opacity'
    copyButton.textContent = 'Use suggestion'
    copyButton.setAttribute('data-question', suggestion.question)
    copyButton.addEventListener('click', e => this.copySuggestion(e))
    
    suggestionDiv.appendChild(questionDiv)
    suggestionDiv.appendChild(reasoningDiv)
    suggestionDiv.appendChild(copyButton)
    
    this.suggestionsTarget.appendChild(suggestionDiv)
  }
  
  renderInsights(data) {
    // Clear existing insights
    this.insightsTarget.innerHTML = ''
    
    const { disc_insights, biases } = data
    
    if (disc_insights.length === 0 && biases.length === 0) {
      const emptyDiv = document.createElement('div')
      emptyDiv.className = 'p-4 text-gray-500 italic'
      emptyDiv.textContent = 'No insights available yet.'
      this.insightsTarget.appendChild(emptyDiv)
      return
    }
    
    // Add DISC insights
    if (disc_insights.length > 0) {
      const discHeader = document.createElement('h3')
      discHeader.className = 'font-semibold text-gray-700 mb-2'
      discHeader.textContent = 'DISC Insights'
      this.insightsTarget.appendChild(discHeader)
      
      disc_insights.forEach(insight => {
        this.addDiscInsight(insight)
      })
    }
    
    // Add bias detections
    if (biases.length > 0) {
      const biasHeader = document.createElement('h3')
      biasHeader.className = 'font-semibold text-gray-700 mt-4 mb-2'
      biasHeader.textContent = 'Potential Biases'
      this.insightsTarget.appendChild(biasHeader)
      
      biases.forEach(bias => {
        this.addBiasDetection(bias)
      })
    }
  }
  
  addDiscInsight(insight) {
    const insightDiv = document.createElement('div')
    insightDiv.className = 'p-3 bg-indigo-50 rounded mb-2'
    
    const traitSpan = document.createElement('span')
    traitSpan.className = 'inline-block px-2 py-1 bg-indigo-100 text-indigo-800 rounded mr-2'
    traitSpan.textContent = `${insight.trait || 'DISC'}`
    
    const insightText = document.createElement('span')
    insightText.className = 'text-gray-800'
    insightText.textContent = insight.insight
    
    insightDiv.appendChild(traitSpan)
    insightDiv.appendChild(insightText)
    
    this.insightsTarget.appendChild(insightDiv)
  }
  
  addBiasDetection(bias) {
    const severityColors = {
      low: 'bg-green-50 text-green-800',
      medium: 'bg-yellow-50 text-yellow-800',
      high: 'bg-red-50 text-red-800'
    }
    
    const biasDiv = document.createElement('div')
    biasDiv.className = `p-3 ${severityColors[bias.severity] || 'bg-yellow-50 text-yellow-800'} rounded mb-2`
    
    const typeDiv = document.createElement('div')
    typeDiv.className = 'font-medium mb-1'
    typeDiv.textContent = bias.bias_type
    
    const explanationDiv = document.createElement('div')
    explanationDiv.className = 'text-sm'
    explanationDiv.textContent = bias.explanation
    
    const suggestionsDiv = document.createElement('div')
    suggestionsDiv.className = 'text-sm mt-1 italic'
    suggestionsDiv.textContent = `Suggestion: ${bias.suggestions[0] || 'None'}`
    
    biasDiv.appendChild(typeDiv)
    biasDiv.appendChild(explanationDiv)
    biasDiv.appendChild(suggestionsDiv)
    
    this.insightsTarget.appendChild(biasDiv)
  }
  
  updateControlsVisibility() {
    // Get all button elements
    const startButton = this.controlsTarget.querySelector('[data-action*="start-session"]')
    const pauseButton = this.controlsTarget.querySelector('[data-action*="pause-session"]')
    const resumeButton = this.controlsTarget.querySelector('[data-action*="resume-session"]')
    const completeButton = this.controlsTarget.querySelector('[data-action*="complete-session"]')
    
    // Hide all buttons first
    if (startButton) startButton.classList.add('hidden')
    if (pauseButton) pauseButton.classList.add('hidden')
    if (resumeButton) resumeButton.classList.add('hidden')
    if (completeButton) completeButton.classList.add('hidden')
    
    // Show appropriate buttons based on status
    switch (this.statusValue) {
      case 'pending':
        if (startButton) startButton.classList.remove('hidden')
        break
      case 'active':
        if (pauseButton) pauseButton.classList.remove('hidden')
        if (completeButton) completeButton.classList.remove('hidden')
        break
      case 'paused':
        if (resumeButton) resumeButton.classList.remove('hidden')
        if (completeButton) completeButton.classList.remove('hidden')
        break
      case 'completed':
        // All buttons remain hidden
        break
    }
    
    // Update status display
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this.statusValue.charAt(0).toUpperCase() + this.statusValue.slice(1)
      
      // Set status color
      this.statusTarget.className = 'px-2 py-1 rounded text-sm font-medium'
      
      switch (this.statusValue) {
        case 'pending':
          this.statusTarget.classList.add('bg-gray-100', 'text-gray-800')
          break
        case 'active':
          this.statusTarget.classList.add('bg-green-100', 'text-green-800')
          break
        case 'paused':
          this.statusTarget.classList.add('bg-yellow-100', 'text-yellow-800')
          break
        case 'completed':
          this.statusTarget.classList.add('bg-blue-100', 'text-blue-800')
          break
      }
    }
  }
  
  // Timer related methods
  startTimer() {
    this.sessionStartTime = new Date()
    this.elapsedSeconds = 0
    this.timerInterval = setInterval(() => this.updateTimer(), 1000)
    this.updateTimer()
  }
  
  pauseTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
  }
  
  resumeTimer() {
    if (!this.timerInterval) {
      this.timerInterval = setInterval(() => this.updateTimer(), 1000)
    }
  }
  
  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
  }
  
  updateTimer() {
    if (!this.hasTimerTarget) return
    
    this.elapsedSeconds++
    const hours = Math.floor(this.elapsedSeconds / 3600)
    const minutes = Math.floor((this.elapsedSeconds % 3600) / 60)
    const seconds = this.elapsedSeconds % 60
    
    this.timerTarget.textContent = [
      hours.toString().padStart(2, '0'),
      minutes.toString().padStart(2, '0'),
      seconds.toString().padStart(2, '0')
    ].join(':')
  }
  
  getCSRFToken() {
    const metaTag = document.querySelector('meta[name="csrf-token"]')
    return metaTag ? metaTag.getAttribute('content') : ''
  }
  
  handleError(message, error) {
    // Silent error handling in production
    // Errors can be logged to a monitoring service if needed
  }
}